import {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {AccountInfo, EventType, PublicClientApplication} from "@azure/msal-browser";
import {MsalProvider} from "@azure/msal-react";
import {AuthProvider as OidcAuthProvider} from "react-oidc-context";
import {WebStorageStateStore} from "oidc-client-ts";
import normalizeUrl from "normalize-url";
import {AuthSetup, getRedirectUri, isAccessedWithinTeams} from "../authConfig";
import {useCookies} from "react-cookie";
import {useAzureAuth} from "./AzureAuthHook";
import {useKeyCloakAuth} from "./KeyCloakAuthHook";
import {UserInfo} from "../support/model";
import {basePath, withBase} from "../utils/basePath";
import {useTeamsAuth} from "./TeamsAuthHook";

interface AuthContextType {
    setSessionIdCookie: () => Promise<void>;
    getToken: () => Promise<string | null | undefined>;
    getHeaders: () => Promise<Record<string, string>>;
    activeOrganization: string | null;
    setOrganization: (organization: string) => void;
    isLoggedIn: () => boolean;
    getActiveAccount: () => any;
    loginPopup: () => Promise<void>;
    logout: () => Promise<void>;
    canLogout: boolean;
    getUserId: () => Promise<string | undefined>;
    userData: UserInfo | undefined;
    isAdmin: boolean;
    isSysAdmin: boolean;
    refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const selectProviderHook = (providerType: AuthSetup["providerType"]) => {
    if (isAccessedWithinTeams) {
        return useTeamsAuth;
    }
    if (providerType === "azure") {
        return useAzureAuth;
    }
    if (providerType === "keycloak") {
        return useKeyCloakAuth;
    }
    throw new Error(`Unknown provider type: ${providerType}`);
};

const AuthHookFunctionsProvider: React.FC<React.PropsWithChildren<{ authSetup: AuthSetup }>> = ({children, authSetup}) => {
    const providerAuthHook = selectProviderHook(authSetup.providerType);
    const [activeOrganization, setActiveOrganization] = useState<string | null>(null);
    const [userData, setUserData] = useState<UserInfo | undefined>(undefined);
    const [cookies, setCookie, removeCookie] = useCookies(["arten_session_id"]);

    const {
        getHeaders: getProviderHeaders,
        getToken: getProviderToken,
        isLoggedIn: isProviderLoggedIn,
        getActiveAccount: getProviderActiveAccount,
        loginPopup: loginProviderPopup,
        logout: logoutProvider,
        canLogout: providerCanLogout,
        getUserId: getProviderUserId,
        refreshToken: refreshProviderToken
    } = providerAuthHook(authSetup);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!isProviderLoggedIn()) {
                setUserData(undefined);
                return;
            }
            try {
                const headers: Record<string, string> = {
                    ...await getProviderHeaders(),
                    "Content-Type": "application/json",
                };
                const userResponse = await fetch(withBase("api/users/me/"), {
                    method: "GET",
                    headers: headers
                });
                if (userResponse.ok) {
                    const userJson = await userResponse.json();
                    setUserData(userJson);
                } else {
                    setUserData(undefined);
                    console.error(`Error fetching user data: ${userResponse.statusText}`);
                }
            } catch (e) {
                console.error(`Error fetching user data: ${e}`);
                setUserData(undefined);
            }
        };
        fetchUserData();
    }, [getProviderHeaders, isProviderLoggedIn]);

    useEffect(() => {
        if (!isProviderLoggedIn()) {
            if (cookies["arten_session_id"] != null) {
                removeCookie("arten_session_id");
            }
        }
    }, [isProviderLoggedIn, cookies, removeCookie]);

    const getHeaders = useCallback(async (): Promise<Record<string, string>> => {
        const headers = await getProviderHeaders();
        if (activeOrganization != null) {
            headers["Active-Organization"] = activeOrganization;
            console.log(`Added Active-Organization header: ${activeOrganization}`);
        }
        return headers;
    }, [getProviderHeaders, activeOrganization]);

    const setOrganization = useCallback((organization: string) => {
        setActiveOrganization(organization);
    }, []);

    const logout = useCallback(async () => {
        await logoutProvider();
        removeCookie("arten_session_id");
    }, [logoutProvider, removeCookie]);

    const setSessionIdCookie = useCallback(async () => {
        const accessToken = await getProviderToken();
        if (cookies["arten_session_id"] != accessToken) {
            setCookie("arten_session_id", accessToken, {path: "/", maxAge: 3600});
        }
    }, [getProviderToken, setCookie, cookies]);

    const refreshToken = useCallback(async () => {
        await refreshProviderToken();
    }, [refreshProviderToken]);

    const contextValue = useMemo(() => {
        const rolesLower = userData?.roles?.map(r => r.toLowerCase()) ?? [];
        return {
            setSessionIdCookie,
            getToken: getProviderToken,
            getHeaders,
            activeOrganization,
            setOrganization,
            isLoggedIn: isProviderLoggedIn,
            getActiveAccount: getProviderActiveAccount,
            loginPopup: loginProviderPopup,
            logout,
            canLogout: providerCanLogout,
            getUserId: getProviderUserId,
            userData: userData,
            isAdmin: (rolesLower.includes("admin") || rolesLower.includes("tester") || rolesLower.includes("sysadmin")),
            isSysAdmin: (rolesLower.includes("tester") || rolesLower.includes("sysadmin")),
            refreshToken: refreshToken,
        } as AuthContextType;
    }, [
        setSessionIdCookie,
        getProviderToken,
        getHeaders,
        setOrganization,
        isProviderLoggedIn,
        getProviderActiveAccount,
        loginProviderPopup,
        logout,
        getProviderUserId,
        userData,
        refreshToken,
        providerCanLogout
    ]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const AuthProvider: React.FC<React.PropsWithChildren<{setupEndpoint: string}>> = ({children, setupEndpoint}) => {
    const [authSetup, setAuthSetupState] = useState<AuthSetup | null>(null);
    const [authError, setAuthError] = useState<Error | null>(null);
    const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

    useEffect(() => {
        let cancelled = false;

        const fetchSetup = async () => {
            const response = await fetch(withBase(setupEndpoint));
            if (!response.ok) {
                throw new Error(`auth setup response was not ok: ${response.status}`);
            }
            const setup = await response.json() as AuthSetup;
            if (cancelled) {
                return;
            }
            setAuthSetupState(setup);
        };

        fetchSetup().catch(error => {
            if (!cancelled) {
                setAuthError(error instanceof Error ? error : new Error(`${error}`));
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (authSetup?.providerType !== "azure") {
            setMsalInstance(null);
            return;
        }

        let cancelled = false;

        const initializeMsal = async () => {
            const instance = new PublicClientApplication(authSetup.providerConfig);
            await instance.initialize();
            if (cancelled) {
                return;
            }
            if (!instance.getActiveAccount()) {
                const accounts = instance.getAllAccounts();
                if (accounts.length > 0) {
                    instance.setActiveAccount(accounts[0]);
                }
            }
            instance.addEventCallback(event => {
                if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                    const account = event.payload as AccountInfo;
                    instance.setActiveAccount(account);
                }
            });
            setMsalInstance(instance);
        };

        initializeMsal().catch(error => {
            if (!cancelled) {
                setAuthError(error instanceof Error ? error : new Error(`${error}`));
            }
        });

        return () => {
            cancelled = true;
        };
    }, [authSetup]);

    if (authError != null) {
        throw authError;
    }

    if (authSetup == null) {
        return null;
    }

    const hookFunctions = (
        <AuthHookFunctionsProvider authSetup={authSetup}>
            {children}
        </AuthHookFunctionsProvider>
    );

    if (isAccessedWithinTeams) {
        return hookFunctions;
    }

    const providerType: string = authSetup.providerType;
    if (providerType === "azure") {
        if (msalInstance == null) {
            return null;
        }
        return (
            <MsalProvider instance={msalInstance}>
                {hookFunctions}
            </MsalProvider>
        );
    }

    if (providerType === "keycloak") {
        const normalizedUrl = normalizeUrl(`${authSetup.providerConfig.auth.serverUrl}/realms/${authSetup.providerConfig.auth.realm}`);
        const silentRedirectUri = normalizeUrl(`${window.location.protocol}//${window.location.host}${basePath}/silent_redirect`);
        return (
            <OidcAuthProvider
                authority={normalizedUrl}
                client_id={authSetup.providerConfig.auth.clientId}
                redirect_uri={getRedirectUri(authSetup)}
                post_logout_redirect_uri={getRedirectUri(authSetup)}
                silent_redirect_uri={silentRedirectUri}
                automaticSilentRenew={true}
                stateStore={new WebStorageStateStore({
                    store: window.localStorage
                })}
                onSigninCallback={(user) => {
                    const returnTo = user?.state as (string | null);
                    if (returnTo != null) {
                        sessionStorage.setItem("keycloak_return_to", returnTo);
                    }
                    window.history.replaceState({}, document.title, window.location.pathname);
                }}
            >
                {hookFunctions}
            </OidcAuthProvider>
        );
    }

    throw new Error(`Unknown provider type: ${providerType ?? "null"}`);
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuthContext must be used within an AuthProvider");
    }
    return context;
};

export class NeedsReLoginError extends Error {
    constructor() {
        super("Token expired, re-login is required");
        this.name = "NeedsReLoginError";
    }

    toString() {
        return this.message;
    }
}

export const useAuth = () => {
    return useAuthContext();
};
